import {Button, Dialog, Flash, FormControl, SegmentedControl, Spinner, Textarea} from '@primer/react'
import {useCallback, useState} from 'react'

import styles from './TriageAgentDebugDialog.module.css'
import {useLoadDebugPrompts, useSubmitDebugTriage} from './use-triage-agent-debug'
import {DEBUG_TRIAGE_TEXT, type TriagePath} from './utils/constants'

type TriageAgentDebugDialogProps = {
  repoOwner: string
  repoName: string
  issueNumber: number
  isOpen: boolean
  onClose: () => void
}

export function TriageAgentDebugDialog({
  repoOwner,
  repoName,
  issueNumber,
  isOpen,
  onClose,
}: TriageAgentDebugDialogProps) {
  const [path, setPath] = useState<TriagePath>('capi')

  // Track user edits separately from loaded prompts
  const [edits, setEdits] = useState<Record<string, string>>({})

  const {
    data: prompts,
    isLoading: isLoadingPrompts,
    isError: isLoadError,
  } = useLoadDebugPrompts(repoOwner, repoName, issueNumber, isOpen)

  const {
    mutate: submitTriage,
    isPending: isSubmitting,
    isSuccess,
    isError: isSubmitError,
    reset: resetSubmit,
  } = useSubmitDebugTriage(repoOwner, repoName)

  // Derive display values: user edits take precedence over loaded prompts
  const developerPrompt = edits.developerPrompt ?? prompts?.capi.developer_prompt ?? ''
  const userPrompt = edits.userPrompt ?? prompts?.capi.user_prompt ?? ''
  const twirpDeveloperPrompt = edits.twirpDeveloperPrompt ?? prompts?.twirp.developer_prompt ?? ''
  const twirpUserPrompt = edits.twirpUserPrompt ?? prompts?.twirp.user_prompt ?? ''

  const handleEdit = useCallback((field: string, value: string) => {
    setEdits(prev => ({...prev, [field]: value}))
  }, [])

  const handleSubmit = useCallback(() => {
    resetSubmit()
    if (path === 'capi') {
      submitTriage({issueNumber, path: 'capi', developerPrompt, userPrompt})
    } else {
      submitTriage({issueNumber, path: 'twirp', developerPrompt: twirpDeveloperPrompt, userPrompt: twirpUserPrompt})
    }
  }, [path, issueNumber, developerPrompt, userPrompt, twirpDeveloperPrompt, twirpUserPrompt, submitTriage, resetSubmit])

  const handlePathChange = useCallback(
    (index: number) => {
      setPath(index === 0 ? 'capi' : 'twirp')
      resetSubmit()
    },
    [resetSubmit],
  )

  if (!isOpen) return null

  return (
    <Dialog title={DEBUG_TRIAGE_TEXT} width="xlarge" onClose={onClose}>
      <div className={styles.dialogBody}>
        <div className={styles.controls}>
          <SegmentedControl aria-label="Triage path" onChange={handlePathChange}>
            <SegmentedControl.Button selected={path === 'capi'}>CAPI</SegmentedControl.Button>
            <SegmentedControl.Button selected={path === 'twirp'}>Twirp</SegmentedControl.Button>
          </SegmentedControl>
        </div>

        {isLoadingPrompts && (
          <div className={styles.spinnerContainer}>
            <Spinner aria-label="Loading prompts" />
          </div>
        )}

        {isLoadError && (
          <Flash variant="danger" className={styles.statusBanner}>
            Failed to load prompts for this issue.
          </Flash>
        )}

        {!isLoadingPrompts && !isLoadError && (
          <>
            {path === 'capi' ? (
              <>
                <FormControl>
                  <FormControl.Label>Developer (system) prompt</FormControl.Label>
                  <Textarea
                    className={styles.promptTextarea}
                    value={developerPrompt}
                    onChange={e => handleEdit('developerPrompt', e.target.value)}
                    aria-label="Developer prompt"
                    data-testid="developer-prompt"
                  />
                </FormControl>
                <FormControl>
                  <FormControl.Label>User prompt</FormControl.Label>
                  <Textarea
                    className={styles.promptTextarea}
                    value={userPrompt}
                    onChange={e => handleEdit('userPrompt', e.target.value)}
                    aria-label="User prompt"
                    data-testid="user-prompt"
                  />
                </FormControl>
              </>
            ) : (
              <>
                <FormControl>
                  <FormControl.Label>Developer prompt</FormControl.Label>
                  <Textarea
                    className={styles.promptTextarea}
                    value={twirpDeveloperPrompt}
                    onChange={e => handleEdit('twirpDeveloperPrompt', e.target.value)}
                    aria-label="Developer prompt"
                    data-testid="twirp-developer-prompt"
                  />
                </FormControl>
                <FormControl>
                  <FormControl.Label>User prompt (problem statement)</FormControl.Label>
                  <Textarea
                    className={styles.promptTextarea}
                    value={twirpUserPrompt}
                    onChange={e => handleEdit('twirpUserPrompt', e.target.value)}
                    aria-label="User prompt"
                    data-testid="twirp-user-prompt"
                  />
                </FormControl>
              </>
            )}

            <div className={styles.actionButtons}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                aria-label={isSubmitting ? 'Submit triage' : undefined}
              >
                {isSubmitting ? <Spinner size="small" /> : 'Submit triage'}
              </Button>
            </div>
          </>
        )}

        {isSuccess && (
          <Flash variant="success" className={styles.statusBanner}>
            {path === 'twirp' ? 'Triage job submitted. You can close this dialog.' : 'Triage completed successfully.'}
          </Flash>
        )}

        {isSubmitError && (
          <Flash variant="danger" className={styles.statusBanner}>
            Failed to submit triage request. Please try again.
          </Flash>
        )}
      </div>
    </Dialog>
  )
}
